/**
 * RichTextEditor
 *
 * mode='whatsapp' (default) — bold / italic / strikethrough toolbar, plain TextInput.
 *                             All existing usages unchanged.
 *
 * mode='product' — full Quill.js editor.
 *   Web   : Quill initialised directly in the page DOM (no iframe).
 *   Native: Quill inside a WebView with postMessage bridge.
 *   Features: bold, italic, underline, strikethrough, text colour, highlight,
 *             font size, H1–H3, bullet & numbered lists, blockquote,
 *             emoji picker, symbol picker.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  FlatList,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { htmlToWhatsAppMarkdown } from '../../utils/htmlToWhatsAppMarkdown';

// ─── Props ────────────────────────────────────────────────────────────────────

/** Mirrors flows/flow-types.ts's VariableGroup shape — duplicated (not imported) to avoid
 * design-system depending on the flow-builder feature; flows/ already imports FROM
 * design-system, so the reverse import would be a circular/layering violation. Structurally
 * typed, so flow-types.ts's real VariableGroup[] is assignable here without any casting. */
export interface RichTextEditorVariableGroup {
  key: string;
  label: string;
  entries: Array<{ path: string; label: string }>;
  freeTextPrefix?: string;
}

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  label?: string;
  showCharCount?: boolean;
  editable?: boolean;
  variant?: 'default' | 'chat';
  showToolbar?: boolean;
  onSubmitShortcut?: () => void;
  mode?: 'whatsapp' | 'product';
  /** When provided (flow builder only), an extra toolbar button opens a small inline variable
   * picker and inserts {{path}} at the cursor — see flow-types.ts's getAvailableVariables.
   * Omitted by every other call site, so this is fully opt-in and doesn't change existing
   * behavior. */
  variables?: RichTextEditorVariableGroup[];
}

// ─── Emoji data ───────────────────────────────────────────────────────────────

const EMOJI_DATA = [
  // Smileys
  {e:'\uD83D\uDE00',n:'grinning'},{e:'\uD83D\uDE03',n:'smiley'},{e:'\uD83D\uDE04',n:'smile'},
  {e:'\uD83D\uDE01',n:'grin'},{e:'\uD83D\uDE06',n:'laughing'},{e:'\uD83D\uDE05',n:'sweat smile'},
  {e:'\uD83D\uDE02',n:'joy'},{e:'\uD83E\uDD23',n:'rofl'},{e:'\uD83D\uDE0A',n:'blush'},
  {e:'\uD83D\uDE07',n:'innocent'},{e:'\uD83D\uDE42',n:'slightly smiling'},{e:'\uD83D\uDE43',n:'upside down'},
  {e:'\uD83D\uDE09',n:'wink'},{e:'\uD83D\uDE0D',n:'heart eyes'},{e:'\uD83E\uDD70',n:'smiling hearts'},
  {e:'\uD83D\uDE18',n:'kiss'},{e:'\uD83D\uDE0E',n:'cool'},{e:'\uD83E\uDD29',n:'star struck'},
  {e:'\uD83E\uDD73',n:'partying'},{e:'\uD83D\uDE0F',n:'smirk'},{e:'\uD83D\uDE12',n:'unamused'},
  {e:'\uD83D\uDE14',n:'pensive'},{e:'\uD83D\uDE22',n:'cry'},{e:'\uD83D\uDE2D',n:'sob'},
  {e:'\uD83D\uDE24',n:'triumph'},{e:'\uD83D\uDE20',n:'angry'},{e:'\uD83D\uDE21',n:'rage'},
  {e:'\uD83E\uDD2F',n:'exploding head'},{e:'\uD83E\uDD7A',n:'pleading'},{e:'\uD83D\uDE31',n:'scream'},
  {e:'\uD83E\uDD14',n:'thinking'},{e:'\uD83E\uDD28',n:'raised eyebrow'},{e:'\uD83D\uDE10',n:'neutral'},
  {e:'\uD83E\uDD17',n:'hugging'},{e:'\uD83E\uDD2B',n:'shushing'},{e:'\uD83D\uDE34',n:'sleeping'},
  {e:'\uD83E\uDD2E',n:'vomiting'},{e:'\uD83E\uDD12',n:'sick'},{e:'\uD83E\uDD11',n:'money mouth'},
  // Hands
  {e:'\uD83D\uDC4D',n:'thumbs up'},{e:'\uD83D\uDC4E',n:'thumbs down'},{e:'\uD83D\uDC4C',n:'ok hand'},
  {e:'\u270C\uFE0F',n:'victory'},{e:'\uD83E\uDD1E',n:'fingers crossed'},{e:'\uD83E\uDD1F',n:'love you'},
  {e:'\uD83E\uDD18',n:'call me'},{e:'\uD83D\uDC4B',n:'wave'},{e:'\uD83E\uDD1D',n:'handshake'},
  {e:'\uD83D\uDE4C',n:'raising hands'},{e:'\uD83D\uDC4F',n:'clapping'},{e:'\uD83D\uDE4F',n:'folded hands'},
  {e:'\uD83D\uDCAA',n:'muscle'},{e:'\uD83D\uDC47',n:'point down'},{e:'\uD83D\uDC46',n:'point up'},
  {e:'\uD83D\uDC49',n:'point right'},{e:'\uD83D\uDC48',n:'point left'},{e:'\u270B',n:'raised hand'},
  // Status & symbols
  {e:'\u2705',n:'check mark'},{e:'\u274C',n:'cross mark'},{e:'\u2B50',n:'star'},
  {e:'\uD83C\uDF1F',n:'glowing star'},{e:'\uD83D\uDCA1',n:'light bulb'},{e:'\uD83D\uDD25',n:'fire'},
  {e:'\u26A1',n:'lightning'},{e:'\uD83D\uDCAF',n:'100 percent'},{e:'\uD83D\uDD11',n:'key'},
  {e:'\uD83D\uDD12',n:'locked'},{e:'\uD83D\uDD13',n:'unlocked'},{e:'\uD83D\uDD0D',n:'search'},
  {e:'\u26A0\uFE0F',n:'warning'},{e:'\uD83D\uDEAB',n:'prohibited'},{e:'\u2757',n:'exclamation'},
  {e:'\u2753',n:'question'},{e:'\uD83D\uDC8E',n:'diamond gem'},{e:'\uD83C\uDFC6',n:'trophy'},
  {e:'\uD83E\uDD47',n:'gold medal'},{e:'\uD83D\uDE80',n:'rocket'},{e:'\uD83C\uDF89',n:'party'},
  {e:'\uD83C\uDF88',n:'balloon'},{e:'\uD83C\uDF08',n:'rainbow'},{e:'\u2744\uFE0F',n:'snowflake'},
  {e:'\uD83D\uDD34',n:'red circle'},{e:'\uD83D\uDFE2',n:'green circle'},{e:'\uD83D\uDD35',n:'blue circle'},
  {e:'\uD83D\uDFE1',n:'yellow circle'},{e:'\uD83D\uDFE0',n:'orange circle'},{e:'\uD83D\uDFE3',n:'purple circle'},
  // Business
  {e:'\uD83D\uDCB0',n:'money bag'},{e:'\uD83D\uDCB5',n:'dollar'},{e:'\uD83D\uDCB3',n:'credit card'},
  {e:'\uD83C\uDFE6',n:'bank'},{e:'\uD83D\uDCC8',n:'chart up'},{e:'\uD83D\uDCC9',n:'chart down'},
  {e:'\uD83D\uDCCA',n:'bar chart'},{e:'\uD83D\uDED2',n:'shopping cart'},{e:'\uD83C\uDFF7\uFE0F',n:'price tag'},
  {e:'\uD83C\uDF81',n:'gift'},{e:'\uD83D\uDCE6',n:'package'},{e:'\uD83D\uDE9A',n:'delivery truck'},
  {e:'\uD83D\uDCE3',n:'megaphone'},{e:'\uD83D\uDD14',n:'bell'},{e:'\uD83D\uDCCB',n:'clipboard'},
  {e:'\uD83D\uDCCC',n:'pushpin'},{e:'\uD83D\uDCBB',n:'laptop'},{e:'\uD83D\uDDA5\uFE0F',n:'desktop'},
  {e:'\uD83D\uDCE7',n:'email'},{e:'\u2699\uFE0F',n:'gear'},{e:'\uD83D\uDCBC',n:'briefcase'},
  {e:'\uD83D\uDD17',n:'link'},{e:'\uD83D\uDCF1',n:'phone'},{e:'\uD83D\uDCF7',n:'camera'},
  // Hearts
  {e:'\u2764\uFE0F',n:'red heart'},{e:'\uD83E\uDDE1',n:'orange heart'},{e:'\uD83D\uDC9B',n:'yellow heart'},
  {e:'\uD83D\uDC9A',n:'green heart'},{e:'\uD83D\uDC99',n:'blue heart'},{e:'\uD83D\uDC9C',n:'purple heart'},
  {e:'\uD83E\uDD0D',n:'white heart'},{e:'\uD83D\uDC94',n:'broken heart'},{e:'\uD83D\uDC95',n:'two hearts'},
  {e:'\uD83D\uDC96',n:'sparkling heart'},{e:'\uD83D\uDC98',n:'heart arrow'},
  // Food & nature
  {e:'\uD83C\uDF55',n:'pizza'},{e:'\uD83C\uDF54',n:'burger'},{e:'\u2615',n:'coffee'},
  {e:'\uD83C\uDF70',n:'cake'},{e:'\uD83C\uDF69',n:'doughnut'},{e:'\uD83C\uDF4E',n:'apple'},
  {e:'\uD83C\uDF53',n:'strawberry'},{e:'\uD83E\uDD51',n:'avocado'},
  {e:'\uD83C\uDF34',n:'palm tree'},{e:'\uD83C\uDF38',n:'cherry blossom'},{e:'\uD83C\uDF3B',n:'sunflower'},
  // Travel
  {e:'\uD83C\uDF0D',n:'globe'},{e:'\u2708\uFE0F',n:'airplane'},{e:'\uD83D\uDE97',n:'car'},
  {e:'\uD83C\uDFE0',n:'house'},{e:'\uD83C\uDFE2',n:'office'},{e:'\uD83D\uDCCD',n:'pin'},
];

// ─── Symbol data ──────────────────────────────────────────────────────────────

const SYM_DATA = [
  // Arrows
  {c:'\u2192',n:'arrow right'},{c:'\u2190',n:'arrow left'},{c:'\u2191',n:'arrow up'},{c:'\u2193',n:'arrow down'},
  {c:'\u2197',n:'arrow up right'},{c:'\u2199',n:'arrow down left'},{c:'\u21D2',n:'double right'},{c:'\u21D0',n:'double left'},
  {c:'\u27F3',n:'refresh'},{c:'\u2194',n:'left right'},{c:'\u2195',n:'up down'},
  // List markers
  {c:'\u2022',n:'bullet'},{c:'\u25E6',n:'open bullet'},{c:'\u25C6',n:'diamond filled'},{c:'\u25C7',n:'diamond outline'},
  {c:'\u25B6',n:'play filled'},{c:'\u25B7',n:'play outline'},{c:'\u25A0',n:'square filled'},{c:'\u25A1',n:'square outline'},
  {c:'\u203A',n:'right bracket'},{c:'\u00BB',n:'double bracket'},{c:'\u2013',n:'en dash'},{c:'\u2014',n:'em dash'},
  // Status
  {c:'\u2713',n:'checkmark'},{c:'\u2717',n:'cross'},{c:'\u2605',n:'star filled'},{c:'\u2606',n:'star outline'},
  {c:'\u2691',n:'flag'},{c:'\u2295',n:'add circle'},{c:'\u2296',n:'remove circle'},{c:'\u2297',n:'close circle'},
  {c:'\u2298',n:'ban'},{c:'\u2726',n:'four star'},{c:'\u203B',n:'reference'},{c:'\u203C',n:'double exclamation'},
  // Business
  {c:'\u00A9',n:'copyright'},{c:'\u00AE',n:'registered'},{c:'\u2122',n:'trademark'},
  {c:'\u20AC',n:'euro'},{c:'\u0024',n:'dollar'},{c:'\u00A3',n:'pound'},{c:'\u00A5',n:'yen'},
  {c:'\u0025',n:'percent'},{c:'\u2030',n:'per mille'},{c:'\u00BD',n:'half'},{c:'\u00BC',n:'quarter'},
  // Math
  {c:'\u00B1',n:'plus minus'},{c:'\u00D7',n:'multiply'},{c:'\u00F7',n:'divide'},{c:'\u2260',n:'not equal'},
  {c:'\u2248',n:'approximately'},{c:'\u221E',n:'infinity'},{c:'\u00B0',n:'degree'},{c:'\u03C0',n:'pi'},
  // Separators
  {c:'\u007C',n:'pipe'},{c:'\u00B7',n:'middle dot'},{c:'\u007E',n:'tilde'},
  {c:'\u00A7',n:'section'},{c:'\u00B6',n:'paragraph'},{c:'\u0023',n:'hash'},{c:'\u002A',n:'asterisk'},
  {c:'\u201C',n:'left quote'},{c:'\u201D',n:'right quote'},{c:'\u2018',n:'left single'},{c:'\u2019',n:'right single'},
  {c:'\u00AB',n:'left guillemet'},{c:'\u2026',n:'ellipsis'},
];

// ─── Quill dark-theme palette ──────────────────────────────────────────────────
// Quill's editor runs as raw HTML/CSS (WebView on native, direct DOM injection on web) — it
// can't reference colors.ts's React Native ColorValue tokens at runtime, so these are plain
// CSS-string equivalents of the same tokens, defined once and interpolated into both templates
// below (buildQuillHtml and WebQuillEditor) so the two stay in sync instead of drifting.
const QUILL_COLORS = {
  surface: String(colors.background.surface),        // editor/toolbar background
  surfaceRaised: String(colors.background.surfaceRaised), // popover background
  border: 'rgba(255, 255, 255, .12)',
  borderSubtle: 'rgba(255, 255, 255, .08)',
  text: String(colors.text.primary),
  textSecondary: String(colors.textLight),
  textMuted: 'rgba(255, 255, 255, .38)',
  hover: 'rgba(255, 255, 255, .08)',
  accent: String(colors.primary),
  onAccent: String(colors.text.onPrimary),
  accentTint: String(colors.primaryTransparent),
  error: String(colors.error),
  shadow: 'rgba(0, 0, 0, .45)',
};

// ─── Quill HTML (native WebView) ──────────────────────────────────────────────

const buildQuillHtml = (
  initialHtml: string,
  placeholder: string,
  maxLength: number,
  editable: boolean,
): string => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<link rel="stylesheet" href="https://cdn.quilljs.com/1.3.7/quill.snow.css">
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body { height: 100%; font-family: -apple-system, BlinkMacSystemFont, sans-serif; background: ${QUILL_COLORS.surface}; }
#wrap { display: flex; flex-direction: column; height: 100%; }
.ql-toolbar.ql-snow {
  border: none; border-bottom: 1px solid ${QUILL_COLORS.borderSubtle}; padding: 4px 8px;
  background: ${QUILL_COLORS.surface}; display: flex; flex-wrap: wrap; align-items: center; gap: 1px;
}
.ql-toolbar.ql-snow .ql-formats { display: flex; align-items: center; margin-right: 2px; }
.ql-container.ql-snow { border: none; font-size: 15px; flex: 1; display: flex; flex-direction: column; }
.ql-editor { flex: 1; line-height: 1.6; color: ${QUILL_COLORS.text}; padding: 10px 12px; }
.ql-editor.ql-blank::before { font-style: normal; color: ${QUILL_COLORS.textMuted}; left: 12px; }
.ql-toolbar.ql-snow button {
  width: 26px; height: 26px; padding: 3px; border-radius: 5px;
  display: flex; align-items: center; justify-content: center; color: ${QUILL_COLORS.textSecondary};
}
.ql-toolbar.ql-snow button:hover { background: ${QUILL_COLORS.hover} !important; }
.ql-toolbar.ql-snow button.ql-active { background: ${QUILL_COLORS.accentTint} !important; }
.ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: ${QUILL_COLORS.accent} !important; }
.ql-toolbar.ql-snow button.ql-active .ql-fill { fill: ${QUILL_COLORS.accent} !important; }
.ql-toolbar.ql-snow .ql-stroke { stroke: ${QUILL_COLORS.textSecondary}; }
.ql-toolbar.ql-snow .ql-fill { fill: ${QUILL_COLORS.textSecondary}; }
.ql-toolbar.ql-snow .ql-picker { font-size: 12px; color: ${QUILL_COLORS.textSecondary}; }
.ql-toolbar.ql-snow .ql-picker-label {
  border: none; border-radius: 5px; padding: 2px 4px; height: 26px;
  display: flex; align-items: center;
}
.ql-toolbar.ql-snow .ql-picker-label:hover { background: ${QUILL_COLORS.hover}; }
.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-label { background: ${QUILL_COLORS.accentTint}; color: ${QUILL_COLORS.accent}; }
.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
  border: 1px solid ${QUILL_COLORS.border}; border-radius: 8px;
  box-shadow: 0 4px 16px ${QUILL_COLORS.shadow}; background: ${QUILL_COLORS.surfaceRaised}; padding: 4px;
}
.ql-picker-item:hover { background: ${QUILL_COLORS.hover}; border-radius: 4px; }
.ql-color-picker .ql-picker-options, .ql-background .ql-picker-options { width: 152px !important; }
.rte-btn {
  width: 26px; height: 26px; border: none; background: none; cursor: pointer;
  border-radius: 5px; display: flex; align-items: center; justify-content: center; padding: 0;
}
.rte-btn:hover { background: ${QUILL_COLORS.hover}; }
.rte-btn-emoji { font-size: 14px; }
.rte-btn-sym { font-size: 13px; font-weight: 700; color: ${QUILL_COLORS.accent}; }
#char-count { font-size: 11px; color: ${QUILL_COLORS.textMuted}; text-align: right; padding: 2px 10px 4px; flex-shrink: 0; }
#char-count.warn { color: ${QUILL_COLORS.error}; }
/* Image resize */
.ql-editor img { cursor: pointer; max-width: 100%; }
.ql-editor img.rte-img-selected { outline: 2px solid ${QUILL_COLORS.accent}; }
.rte-resize-handle {
  position: fixed; width: 10px; height: 10px; background: ${QUILL_COLORS.accent};
  border: 2px solid ${QUILL_COLORS.surface}; border-radius: 2px; cursor: se-resize; z-index: 10000;
  box-shadow: 0 1px 4px ${QUILL_COLORS.shadow};
}
/* Popover */
.pop {
  display: none; position: fixed; background: ${QUILL_COLORS.surfaceRaised};
  border: 1px solid ${QUILL_COLORS.border}; border-radius: 10px;
  box-shadow: 0 8px 32px ${QUILL_COLORS.shadow}; z-index: 9999;
  width: 300px; flex-direction: column;
}
.pop.open { display: flex; }
.pop-hdr { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px 6px; border-bottom: 1px solid ${QUILL_COLORS.borderSubtle}; flex-shrink: 0; }
.pop-hdr span { font-size: 13px; font-weight: 600; color: ${QUILL_COLORS.text}; }
.pop-hdr button { background: none; border: none; cursor: pointer; font-size: 16px; color: ${QUILL_COLORS.textSecondary}; border-radius: 4px; padding: 2px 5px; }
.pop-hdr button:hover { background: ${QUILL_COLORS.hover}; color: ${QUILL_COLORS.text}; }
.pop-search { display: flex; align-items: center; gap: 6px; border: 1px solid ${QUILL_COLORS.border}; border-radius: 7px; margin: 6px 10px; padding: 5px 8px; flex-shrink: 0; }
.pop-search input { flex: 1; border: none; outline: none; font-size: 12px; color: ${QUILL_COLORS.text}; background: none; }
.pop-search input::placeholder { color: ${QUILL_COLORS.textMuted}; }
.pop-scroll { overflow-y: auto; padding: 4px 10px 10px; max-height: 240px; }
.sec-lbl { font-size: 9px; text-transform: uppercase; letter-spacing: .8px; color: ${QUILL_COLORS.textMuted}; margin: 8px 0 4px; font-weight: 600; }
.grid { display: flex; flex-wrap: wrap; gap: 2px; }
.gi { width: 38px; height: 38px; border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; background: transparent; }
.gi:hover { background: ${QUILL_COLORS.accentTint}; }
.gi-e { font-size: 26px; line-height: 1; }
.gi-s { font-size: 20px; font-family: monospace; color: ${QUILL_COLORS.textSecondary}; line-height: 1; }
.pop-empty { color: ${QUILL_COLORS.textMuted}; font-size: 12px; text-align: center; padding: 16px 0; }
</style>
</head>
<body>
<div id="wrap">
  <div id="toolbar">
    <span class="ql-formats">
      <select class="ql-header"><option value="1">H1</option><option value="2">H2</option><option value="3">H3</option><option selected></option></select>
      <select class="ql-size"><option value="small">Small</option><option selected>Normal</option><option value="large">Large</option><option value="huge">Huge</option></select>
    </span>
    <span class="ql-formats">
      <button class="ql-bold" title="Bold"></button>
      <button class="ql-italic" title="Italic"></button>
      <button class="ql-underline" title="Underline"></button>
      <button class="ql-strike" title="Strikethrough"></button>
    </span>
    <span class="ql-formats">
      <select class="ql-color" title="Text colour"></select>
      <select class="ql-background" title="Highlight"></select>
    </span>
    <span class="ql-formats">
      <button class="ql-list" value="ordered"></button>
      <button class="ql-list" value="bullet"></button>
      <button class="ql-blockquote"></button>
      <button class="ql-indent" value="-1"></button>
      <button class="ql-indent" value="+1"></button>
    </span>
    <span class="ql-formats">
      <select class="ql-align"></select>
    </span>
    <span class="ql-formats">
      <button id="btn-link" class="rte-btn rte-btn-sym" title="Insert link">&#128279;</button>
      <button id="btn-img" class="rte-btn rte-btn-sym" title="Insert image">&#128247;</button>
      <button id="btn-emoji" class="rte-btn rte-btn-emoji" title="Emoji">&#128522;</button>
      <button id="btn-sym" class="rte-btn rte-btn-sym" title="Symbols">&#937;</button>
    </span>
    <span class="ql-formats">
      <button class="ql-clean" title="Clear formatting"></button>
    </span>
  </div>
  <div id="editor"></div>
  <div id="char-count">0 / ${maxLength}</div>
</div>

<!-- Emoji popover -->
<div id="pop-emoji" class="pop">
  <div class="pop-hdr"><span>Emoji</span><button id="pc-emoji">&#x2715;</button></div>
  <div class="pop-search"><span style="color:${QUILL_COLORS.textMuted};font-size:12px">&#128269;</span><input id="ps-emoji" placeholder="Search emoji\u2026" autocomplete="off" spellcheck="false"></div>
  <div class="pop-scroll" id="pg-emoji"></div>
</div>

<!-- Symbol popover -->
<div id="pop-sym" class="pop">
  <div class="pop-hdr"><span>Symbols</span><button id="pc-sym">&#x2715;</button></div>
  <div class="pop-search"><span style="color:${QUILL_COLORS.textMuted};font-size:12px">&#128269;</span><input id="ps-sym" placeholder="Search symbols\u2026" autocomplete="off" spellcheck="false"></div>
  <div class="pop-scroll" id="pg-sym"></div>
</div>

<!-- Link popover -->
<div id="pop-link" class="pop" style="width:320px">
  <div class="pop-hdr"><span>Insert Link</span><button id="pc-link">&#x2715;</button></div>
  <div style="padding:10px 12px;display:flex;flex-direction:column;gap:8px">
    <input id="link-text" placeholder="Link text (optional)" autocomplete="off" spellcheck="false" style="border:1px solid ${QUILL_COLORS.border};border-radius:7px;padding:7px 10px;font-size:13px;color:${QUILL_COLORS.text};background:${QUILL_COLORS.surface};outline:none;width:100%;box-sizing:border-box">
    <input id="link-url" placeholder="https://\u2026" autocomplete="off" spellcheck="false" style="border:1px solid ${QUILL_COLORS.border};border-radius:7px;padding:7px 10px;font-size:13px;color:${QUILL_COLORS.text};background:${QUILL_COLORS.surface};outline:none;width:100%;box-sizing:border-box">
    <button id="link-insert" style="background:${QUILL_COLORS.accent};color:${QUILL_COLORS.onAccent};border:none;border-radius:7px;padding:8px;font-size:13px;font-weight:600;cursor:pointer">Insert</button>
  </div>
</div>

<!-- Image popover -->
<div id="pop-img" class="pop" style="width:320px">
  <div class="pop-hdr"><span>Insert Image</span><button id="pc-img">&#x2715;</button></div>
  <div style="padding:10px 12px;display:flex;flex-direction:column;gap:8px">
    <input id="img-url" placeholder="https://\u2026 (image URL)" autocomplete="off" spellcheck="false" style="border:1px solid ${QUILL_COLORS.border};border-radius:7px;padding:7px 10px;font-size:13px;color:${QUILL_COLORS.text};background:${QUILL_COLORS.surface};outline:none;width:100%;box-sizing:border-box">
    <input id="img-alt" placeholder="Alt text (optional)" autocomplete="off" spellcheck="false" style="border:1px solid ${QUILL_COLORS.border};border-radius:7px;padding:7px 10px;font-size:13px;color:${QUILL_COLORS.text};background:${QUILL_COLORS.surface};outline:none;width:100%;box-sizing:border-box">
    <button id="img-insert" style="background:${QUILL_COLORS.accent};color:${QUILL_COLORS.onAccent};border:none;border-radius:7px;padding:8px;font-size:13px;font-weight:600;cursor:pointer">Insert</button>
  </div>
</div>

<script src="https://cdn.quilljs.com/1.3.7/quill.min.js"></script>
<script>
var MAX = ${maxLength};
var savedRange = null;

var quill = new Quill('#editor', {
  theme: 'snow',
  modules: { toolbar: '#toolbar' },
  placeholder: '${placeholder.replace(/'/g, "\\'")}',
  readOnly: ${!editable},
});

${initialHtml ? `
(function(){
  var cb = quill.getModule('clipboard');
  quill.setContents(cb.convert(${JSON.stringify(initialHtml)}), 'silent');
})();` : ''}

var countEl = document.getElementById('char-count');
function updateCount() {
  var n = Math.max(0, quill.getLength() - 1);
  countEl.textContent = n + ' / ' + MAX;
  countEl.className = (MAX - n < Math.max(MAX * 0.1, 50)) ? 'warn' : '';
}
updateCount();
quill.on('text-change', function() {
  var n = quill.getLength() - 1;
  if (n > MAX) { quill.deleteText(MAX, n - MAX); return; }
  updateCount();
  notify();
});

function notify() {
  var msg = JSON.stringify({ type: 'change', html: quill.root.innerHTML });
  if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(msg);
  else window.parent.postMessage(msg, '*');
}

document.addEventListener('message', function(e) { handleCmd(e.data); });
window.addEventListener('message', function(e) { handleCmd(e.data); });
function handleCmd(raw) {
  try {
    var cmd = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (cmd.type === 'setContent') {
      var cb2 = quill.getModule('clipboard');
      quill.setContents(cb2.convert(cmd.html || ''), 'silent');
      updateCount();
    } else if (cmd.type === 'insertText') {
      var r = quill.getSelection(true);
      quill.insertText(r.index, cmd.text);
      quill.setSelection(r.index + cmd.text.length);
    }
  } catch(e) {}
}

/* ── Popover helpers ── */
var EMOJI = ${JSON.stringify(EMOJI_DATA)};
var SYM   = ${JSON.stringify(SYM_DATA)};

function positionPop(pop, btn) {
  void btn.offsetHeight; // force layout
  var br = btn.getBoundingClientRect();
  if (!br.width && !br.height) return;
  var vw = window.innerWidth, vh = window.innerHeight;
  var pw = parseInt(pop.style.width) || 300;
  var ph = 200;
  var left = Math.min(Math.max(br.left, 8), vw - pw - 8);
  var top  = br.bottom + 4;
  if (top + ph > vh - 8) top = br.top - ph - 4;
  pop.style.left = left + 'px';
  pop.style.top  = top  + 'px';
}

function closeAll() {
  document.querySelectorAll('.pop.open').forEach(function(p){ p.classList.remove('open'); });
}

document.addEventListener('mousedown', function(e) {
  if (!e.target.closest('.pop') && !e.target.closest('.rte-btn')) closeAll();
}, true);

function renderGrid(gridEl, data, isEmoji) {
  var html = '<div class="grid">';
  data.forEach(function(it) {
    var val = isEmoji ? it.e : it.c;
    var inner = isEmoji
      ? '<span class="gi-e">' + val + '</span>'
      : '<span class="gi-s">' + val + '</span>';
    html += '<div class="gi" data-val="' + val + '" title="' + it.n + '">' + inner + '</div>';
  });
  html += '</div>';
  gridEl.innerHTML = html;
  gridEl.querySelectorAll('.gi').forEach(function(el) {
    el.addEventListener('click', function() {
      var v = el.dataset.val;
      var r = savedRange || { index: Math.max(0, quill.getLength() - 1) };
      quill.insertText(r.index, v);
      quill.setSelection(r.index + v.length);
      closeAll();
      notify();
    });
  });
}

function openPop(popId, gridId, searchId, closeId, data, isEmoji, btn) {
  savedRange = quill.getSelection();
  var pop    = document.getElementById(popId);
  var grid   = document.getElementById(gridId);
  var search = document.getElementById(searchId);
  var wasOpen = pop.classList.contains('open');
  closeAll();
  if (wasOpen) return;
  search.value = '';
  renderGrid(grid, data, isEmoji);
  positionPop(pop, btn);
  pop.classList.add('open');
  search.addEventListener('input', function() {
    var q = search.value.trim().toLowerCase();
    var filtered = q ? data.filter(function(it){ return it.n.includes(q) || (it.e||it.c||'').includes(q); }) : data;
    if (filtered.length === 0) { grid.innerHTML = '<div class="pop-empty">No results</div>'; }
    else renderGrid(grid, filtered, isEmoji);
  }, { once: false });
  document.getElementById(closeId).onclick = function(){ pop.classList.remove('open'); quill.focus(); };
}

document.getElementById('btn-emoji').addEventListener('click', function(e) {
  e.stopPropagation();
  openPop('pop-emoji','pg-emoji','ps-emoji','pc-emoji', EMOJI, true, e.currentTarget);
});
document.getElementById('btn-sym').addEventListener('click', function(e) {
  e.stopPropagation();
  openPop('pop-sym','pg-sym','ps-sym','pc-sym', SYM, false, e.currentTarget);
});

/* ── Link popover ── */
document.getElementById('btn-link').addEventListener('click', function(e) {
  e.stopPropagation();
  savedRange = quill.getSelection();
  var pop = document.getElementById('pop-link');
  var wasOpen = pop.classList.contains('open');
  closeAll();
  if (wasOpen) return;
  // Pre-fill text with selection
  var selText = '';
  if (savedRange && savedRange.length > 0) {
    selText = quill.getText(savedRange.index, savedRange.length);
  }
  document.getElementById('link-text').value = selText;
  document.getElementById('link-url').value = '';
  positionPop(pop, e.currentTarget);
  pop.classList.add('open');
  setTimeout(function(){ document.getElementById('link-url').focus(); }, 50);
});
document.getElementById('pc-link').onclick = function(){ document.getElementById('pop-link').classList.remove('open'); quill.focus(); };
document.getElementById('link-insert').addEventListener('click', function() {
  var url  = document.getElementById('link-url').value.trim();
  var text = document.getElementById('link-text').value;
  if (!url) return;
  var r = savedRange || { index: Math.max(0, quill.getLength() - 1), length: 0 };
  if (r.length > 0) {
    // Format existing selection as link
    quill.formatText(r.index, r.length, 'link', url);
  } else {
    // Insert new linked text
    var display = text || url;
    quill.insertText(r.index, display, 'link', url);
    quill.setSelection(r.index + display.length);
  }
  closeAll();
  notify();
});

/* ── Image popover ── */
document.getElementById('btn-img').addEventListener('click', function(e) {
  e.stopPropagation();
  savedRange = quill.getSelection();
  var pop = document.getElementById('pop-img');
  var wasOpen = pop.classList.contains('open');
  closeAll();
  if (wasOpen) return;
  document.getElementById('img-url').value = '';
  document.getElementById('img-alt').value = '';
  positionPop(pop, e.currentTarget);
  pop.classList.add('open');
  setTimeout(function(){ document.getElementById('img-url').focus(); }, 50);
});
document.getElementById('pc-img').onclick = function(){ document.getElementById('pop-img').classList.remove('open'); quill.focus(); };
document.getElementById('img-insert').addEventListener('click', function() {
  var url = document.getElementById('img-url').value.trim();
  if (!url) return;
  var r = savedRange || { index: Math.max(0, quill.getLength() - 1) };
  quill.insertEmbed(r.index, 'image', url);
  quill.setSelection(r.index + 1);
  closeAll();
  notify();
});

/* ── Image resize ── */
(function() {
  var selImg = null, handle = null, startX, startY, startW, startH;

  function removeHandle() {
    if (handle && handle.parentNode) handle.parentNode.removeChild(handle);
    handle = null;
  }

  function placeHandle(img) {
    removeHandle();
    handle = document.createElement('div');
    handle.className = 'rte-resize-handle';
    document.body.appendChild(handle);
    updateHandlePos(img);
    handle.addEventListener('mousedown', onHandleDown);
    handle.addEventListener('touchstart', onHandleDown, { passive: false });
  }

  function updateHandlePos(img) {
    if (!handle || !img) return;
    var r = img.getBoundingClientRect();
    handle.style.left = (r.right - 6) + 'px';
    handle.style.top  = (r.bottom - 6) + 'px';
  }

  function selectImg(img) {
    if (selImg) selImg.classList.remove('rte-img-selected');
    selImg = img;
    img.classList.add('rte-img-selected');
    placeHandle(img);
  }

  function deselect() {
    if (selImg) selImg.classList.remove('rte-img-selected');
    selImg = null;
    removeHandle();
  }

  function onHandleDown(e) {
    e.preventDefault();
    var touch = e.touches ? e.touches[0] : e;
    startX = touch.clientX; startY = touch.clientY;
    startW = selImg.offsetWidth; startH = selImg.offsetHeight;
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('touchmove', onDrag, { passive: false });
    document.addEventListener('touchend', onDragEnd);
  }

  function onDrag(e) {
    if (!selImg) return;
    e.preventDefault();
    var touch = e.touches ? e.touches[0] : e;
    var dx = touch.clientX - startX;
    var newW = Math.max(40, startW + dx);
    selImg.style.width = newW + 'px';
    selImg.style.height = 'auto';
    updateHandlePos(selImg);
  }

  function onDragEnd() {
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchmove', onDrag);
    document.removeEventListener('touchend', onDragEnd);
    notify();
  }

  quill.root.addEventListener('click', function(e) {
    var t = e.target;
    if (t && t.tagName === 'IMG') { selectImg(t); }
    else { deselect(); }
  });

  document.addEventListener('click', function(e) {
    if (!e.target.closest('.ql-editor') && e.target !== handle) deselect();
  });

  window.addEventListener('scroll', function() { if (selImg) updateHandlePos(selImg); }, true);
  window.addEventListener('resize', function() { if (selImg) updateHandlePos(selImg); });
})();
</script>
</body>
</html>`;

// ─── Toolbar button (WhatsApp mode) ───────────────────────────────────────────

const ToolBtn: React.FC<{
  onPress: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}> = ({ onPress, disabled, children, title }) => {
  const tbStyles = useThemedStyles(getTbStyles);
  return (
  <TouchableOpacity
    style={[tbStyles.btn, disabled && tbStyles.disabled]}
    onPress={onPress}
    disabled={disabled}
    hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
    accessibilityLabel={title}
  >
    {children}
  </TouchableOpacity>
  );
};

const getTbStyles = () => StyleSheet.create({
  btn: { width: 32, height: 32, borderRadius: 6, justifyContent: 'center', alignItems: 'center' },
  disabled: { opacity: 0.35 },
});

// ─── Web Quill editor ─────────────────────────────────────────────────────────

let _editorCount = 0;

const WebQuillEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  maxLength: number;
  editable: boolean;
  height: number;
}> = ({ value, onChange, placeholder, maxLength, editable, height }) => {
  const containerRef = useRef<any>(null);
  const id = useRef(`rte${++_editorCount}`).current;
  const isFocused = useRef(false);
  const lastValue = useRef(value);

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) return;

    // Quill CSS
    if (!document.getElementById('ql-css')) {
      const l = document.createElement('link');
      l.id = 'ql-css'; l.rel = 'stylesheet';
      l.href = 'https://cdn.quilljs.com/1.3.7/quill.snow.css';
      document.head.appendChild(l);
    }

    const init = () => {
      const Q = (window as any).Quill;
      if (!Q) return;

      const tid = `${id}-tb`, eid = `${id}-ed`, cid = `${id}-ct`;

      containerRef.current.innerHTML = `
        <div style="display:flex;flex-direction:column;height:100%;background:${QUILL_COLORS.surface};border-radius:10px;overflow:visible">
          <div id="${tid}" style="flex-shrink:0"></div>
          <div id="${eid}" style="flex:1;overflow-y:auto;border-top:1px solid ${QUILL_COLORS.borderSubtle}"></div>
          <div id="${cid}" style="font-size:11px;color:${QUILL_COLORS.textMuted};text-align:right;padding:2px 10px 4px;flex-shrink:0">0 / ${maxLength}</div>
        </div>`;

      // Shared CSS (once)
      if (!document.getElementById('rte-ql-css')) {
        const s = document.createElement('style');
        s.id = 'rte-ql-css';
        s.textContent = `
          .ql-toolbar.ql-snow{border:none;border-bottom:1px solid ${QUILL_COLORS.borderSubtle};padding:4px 8px;background:${QUILL_COLORS.surface};display:flex;flex-wrap:wrap;align-items:center;gap:1px}
          .ql-toolbar.ql-snow .ql-formats{display:flex;align-items:center;margin-right:2px}
          .ql-container.ql-snow{border:none;font-size:15px}
          .ql-editor{line-height:1.6;color:${QUILL_COLORS.text};padding:10px 12px}
          .ql-editor.ql-blank::before{font-style:normal;color:${QUILL_COLORS.textMuted};left:12px}
          .ql-toolbar.ql-snow button{width:26px;height:26px;padding:3px;border-radius:5px;display:flex;align-items:center;justify-content:center;color:${QUILL_COLORS.textSecondary}}
          .ql-toolbar.ql-snow button:hover{background:${QUILL_COLORS.hover}!important}
          .ql-toolbar.ql-snow button.ql-active{background:${QUILL_COLORS.accentTint}!important}
          .ql-toolbar.ql-snow button.ql-active .ql-stroke{stroke:${QUILL_COLORS.accent}!important}
          .ql-toolbar.ql-snow button.ql-active .ql-fill{fill:${QUILL_COLORS.accent}!important}
          .ql-toolbar.ql-snow .ql-stroke{stroke:${QUILL_COLORS.textSecondary}}
          .ql-toolbar.ql-snow .ql-fill{fill:${QUILL_COLORS.textSecondary}}
          .ql-toolbar.ql-snow .ql-picker{font-size:12px;color:${QUILL_COLORS.textSecondary}}
          .ql-toolbar.ql-snow .ql-picker-label{border:none;border-radius:5px;padding:2px 4px;height:26px;display:flex;align-items:center}
          .ql-toolbar.ql-snow .ql-picker-label:hover{background:${QUILL_COLORS.hover}}
          .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-label{background:${QUILL_COLORS.accentTint};color:${QUILL_COLORS.accent}}
          .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options{border:1px solid ${QUILL_COLORS.border};border-radius:8px;box-shadow:0 4px 16px ${QUILL_COLORS.shadow};background:${QUILL_COLORS.surfaceRaised};padding:4px}
          .ql-picker-item:hover{background:${QUILL_COLORS.hover};border-radius:4px}
          .ql-color-picker .ql-picker-options,.ql-background .ql-picker-options{width:152px!important}
          .rte-btn{width:26px;height:26px;border:none;background:none;cursor:pointer;border-radius:5px;display:flex;align-items:center;justify-content:center;padding:0}
          .rte-btn:hover{background:${QUILL_COLORS.hover}}
          .rte-btn-emoji{font-size:14px}
          .rte-btn-sym{font-size:13px;font-weight:700;color:${QUILL_COLORS.accent}}
          .rte-pop{display:none;position:fixed;background:${QUILL_COLORS.surfaceRaised};border:1px solid ${QUILL_COLORS.border};border-radius:10px;box-shadow:0 8px 32px ${QUILL_COLORS.shadow};z-index:99999;width:300px;flex-direction:column}
          .rte-pop.open{display:flex}
          .rte-pop-hdr{display:flex;align-items:center;justify-content:space-between;padding:10px 12px 6px;border-bottom:1px solid ${QUILL_COLORS.borderSubtle};flex-shrink:0}
          .rte-pop-hdr span{font-size:13px;font-weight:600;color:${QUILL_COLORS.text}}
          .rte-pop-hdr button{background:none;border:none;cursor:pointer;font-size:16px;color:${QUILL_COLORS.textSecondary};border-radius:4px;padding:2px 5px}
          .rte-pop-hdr button:hover{background:${QUILL_COLORS.hover};color:${QUILL_COLORS.text}}
          .rte-pop-search{display:flex;align-items:center;gap:6px;border:1px solid ${QUILL_COLORS.border};border-radius:7px;margin:6px 10px;padding:5px 8px;flex-shrink:0}
          .rte-pop-search input{flex:1;border:none;outline:none;font-size:12px;color:${QUILL_COLORS.text};background:none}
          .rte-pop-search input::placeholder{color:${QUILL_COLORS.textMuted}}
          .rte-pop-scroll{overflow-y:auto;padding:4px 10px 10px;max-height:260px}
          .rte-grid{display:flex;flex-wrap:wrap;gap:2px}
          .rte-gi{width:38px;height:38px;border-radius:6px;display:flex;align-items:center;justify-content:center;cursor:pointer;background:transparent}
          .rte-gi:hover{background:${QUILL_COLORS.accentTint}}
          .rte-gi-e{font-size:26px;line-height:1}
          .rte-gi-s{font-size:20px;font-family:monospace;color:${QUILL_COLORS.textSecondary};line-height:1}
          .rte-empty{color:${QUILL_COLORS.textMuted};font-size:12px;text-align:center;padding:16px 0}
          .ql-editor img{cursor:pointer;max-width:100%}
          .ql-editor img.rte-img-selected{outline:2px solid ${QUILL_COLORS.accent}}
          .rte-resize-handle{position:fixed;width:10px;height:10px;background:${QUILL_COLORS.accent};border:2px solid ${QUILL_COLORS.surface};border-radius:2px;cursor:se-resize;z-index:99999;box-shadow:0 1px 4px ${QUILL_COLORS.shadow}}
        `;
        document.head.appendChild(s);
      }

      // Build toolbar HTML with custom buttons
      document.getElementById(tid)!.innerHTML = `
        <span class="ql-formats">
          <select class="ql-header"><option value="1">H1</option><option value="2">H2</option><option value="3">H3</option><option selected></option></select>
          <select class="ql-size"><option value="small">Small</option><option selected>Normal</option><option value="large">Large</option><option value="huge">Huge</option></select>
        </span>
        <span class="ql-formats">
          <button class="ql-bold"></button><button class="ql-italic"></button>
          <button class="ql-underline"></button><button class="ql-strike"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-color"></select><select class="ql-background"></select>
        </span>
        <span class="ql-formats">
          <button class="ql-list" value="ordered"></button>
          <button class="ql-list" value="bullet"></button>
          <button class="ql-blockquote"></button>
          <button class="ql-indent" value="-1"></button>
          <button class="ql-indent" value="+1"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-align"></select>
        </span>
        <span class="ql-formats">
          <button id="${id}-btn-link" class="rte-btn rte-btn-sym" title="Insert link">&#128279;</button>
          <button id="${id}-btn-img" class="rte-btn rte-btn-sym" title="Insert image">&#128247;</button>
          <button id="${id}-btn-emoji" class="rte-btn rte-btn-emoji" title="Emoji">&#128522;</button>
          <button id="${id}-btn-sym" class="rte-btn rte-btn-sym" title="Symbols">&#937;</button>
        </span>
        <span class="ql-formats"><button class="ql-clean"></button></span>
      `;

      const quill = new Q(`#${eid}`, {
        theme: 'snow',
        modules: { toolbar: `#${tid}` },
        placeholder,
        readOnly: !editable,
      });

      if (value) {
        const cb = quill.getModule('clipboard');
        quill.setContents(cb.convert(value), 'silent');
      }

      const countEl = document.getElementById(cid)!;
      const updateCount = () => {
        const n = Math.max(0, quill.getLength() - 1);
        countEl.textContent = `${n} / ${maxLength}`;
        countEl.style.color = (maxLength - n < Math.max(maxLength * 0.1, 50)) ? QUILL_COLORS.error : QUILL_COLORS.textMuted;
      };
      updateCount();

      quill.on('text-change', () => {
        const n = quill.getLength() - 1;
        if (n > maxLength) { quill.deleteText(maxLength, n - maxLength); return; }
        updateCount();
        const html = quill.root.innerHTML;
        lastValue.current = html;
        onChange(html);
      });

      quill.root.addEventListener('focus', () => { isFocused.current = true; });
      quill.root.addEventListener('blur',  () => { isFocused.current = false; });
      (containerRef.current as any)._quill = quill;

      // ── Popovers ────────────────────────────────────────────────────────────
      let savedRange: any = null;

      const EMOJI = EMOJI_DATA;
      const SYM   = SYM_DATA;

      const positionPop = (pop: HTMLElement, btn: HTMLElement) => {
        // Force layout so getBoundingClientRect is accurate
        void btn.offsetHeight;
        const br = btn.getBoundingClientRect();
        // If button has no size it's probably not rendered yet — skip
        if (!br.width && !br.height) return;
        const vw = window.innerWidth, vh = window.innerHeight;
        const pw = parseInt(pop.style.width) || 300;
        const ph = 200; // safe estimate for flip logic
        const left = Math.min(Math.max(br.left, 8), vw - pw - 8);
        const top  = br.bottom + 4 + ph > vh - 8 ? br.top - ph - 4 : br.bottom + 4;
        pop.style.left = left + 'px';
        pop.style.top  = top  + 'px';
      };

      const closeAll = () => document.querySelectorAll('.rte-pop.open').forEach(p => p.classList.remove('open'));

      document.addEventListener('mousedown', (e: MouseEvent) => {
        if (!(e.target as HTMLElement).closest('.rte-pop') &&
            !(e.target as HTMLElement).closest('.rte-btn')) closeAll();
      }, true);

      const makePop = (title: string) => {
        const pop = document.createElement('div');
        pop.className = 'rte-pop';
        pop.style.position = 'fixed';
        const searchPlaceholder = title === 'Emoji' ? 'Search emoji\u2026' : 'Search symbols\u2026';
        pop.innerHTML = `
          <div class="rte-pop-hdr">
            <span>${title}</span>
            <button class="rte-pop-close">&#x2715;</button>
          </div>
          <div class="rte-pop-search">
            <span style="color:${QUILL_COLORS.textMuted};font-size:12px">&#128269;</span>
            <input class="rte-pop-search-input" placeholder="${searchPlaceholder}" autocomplete="off" spellcheck="false">
          </div>
          <div class="rte-pop-scroll rte-pop-grid"></div>
        `;
        document.body.appendChild(pop);
        pop.querySelector('.rte-pop-close')!.addEventListener('click', () => { pop.classList.remove('open'); quill.focus(); });
        return pop;
      };

      const renderGrid = (gridEl: HTMLElement, data: any[], isEmoji: boolean) => {
        gridEl.innerHTML = `<div class="rte-grid">${
          data.map(it => {
            const val = isEmoji ? it.e : it.c;
            const inner = isEmoji
              ? `<span class="rte-gi-e">${val}</span>`
              : `<span class="rte-gi-s">${val}</span>`;
            return `<div class="rte-gi" data-val="${val}" title="${it.n}">${inner}</div>`;
          }).join('')
        }</div>`;
        gridEl.querySelectorAll('.rte-gi').forEach(el => {
          el.addEventListener('click', () => {
            const val = (el as HTMLElement).dataset.val!;
            const r = savedRange || { index: Math.max(0, quill.getLength() - 1) };
            quill.insertText(r.index, val);
            quill.setSelection(r.index + val.length);
            closeAll();
            const html = quill.root.innerHTML;
            lastValue.current = html;
            onChange(html);
          });
        });
      };

      const openPop = (pop: HTMLElement, btn: HTMLElement, data: any[], isEmoji: boolean) => {
        savedRange = quill.getSelection();
        const wasOpen = pop.classList.contains('open');
        closeAll();
        if (wasOpen) return;
        const grid    = pop.querySelector('.rte-pop-grid') as HTMLElement;
        const search  = pop.querySelector('.rte-pop-search-input') as HTMLInputElement;
        search.value  = '';
        renderGrid(grid, data, isEmoji);
        positionPop(pop, btn);
        pop.classList.add('open');
        // Re-attach search listener fresh each open
        const onSearch = () => {
          const q = search.value.trim().toLowerCase();
          const filtered = q ? data.filter(it => it.n.includes(q) || (it.e || it.c || '').includes(q)) : data;
          if (filtered.length === 0) { grid.innerHTML = '<div class="rte-empty">No results</div>'; }
          else renderGrid(grid, filtered, isEmoji);
        };
        search.removeEventListener('input', onSearch);
        search.addEventListener('input', onSearch);
      };

      const emojiPop = makePop('Emoji');
      const symPop   = makePop('Symbols');

      document.getElementById(`${id}-btn-emoji`)!.addEventListener('click', e => {
        e.stopPropagation();
        openPop(emojiPop, e.currentTarget as HTMLElement, EMOJI, true);
      });
      document.getElementById(`${id}-btn-sym`)!.addEventListener('click', e => {
        e.stopPropagation();
        openPop(symPop, e.currentTarget as HTMLElement, SYM, false);
      });

      // Keep pops anchored on scroll/resize
      const reposition = () => {
        const openPops = document.querySelectorAll('.rte-pop.open');
        openPops.forEach(p => {
          const pop = p as HTMLElement;
          const btnId = pop.dataset.anchorId;
          if (btnId) {
            const btn = document.getElementById(btnId);
            if (btn) positionPop(pop, btn);
          }
        });
      };
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);

      // ── Link popover ──────────────────────────────────────────────────────
      const makeInputPop = (title: string) => {
        const pop = document.createElement('div');
        pop.className = 'rte-pop';
        pop.style.width = '320px';
        pop.innerHTML = `
          <div class="rte-pop-hdr">
            <span>${title}</span>
            <button class="rte-pop-close">\u00D7</button>
          </div>
          <div class="rte-input-body"></div>`;
        document.body.appendChild(pop);
        pop.querySelector('.rte-pop-close')!.addEventListener('click', () => { pop.classList.remove('open'); quill.focus(); });
        return pop;
      };

      const inputStyle = `border:1px solid ${QUILL_COLORS.border};border-radius:7px;padding:7px 10px;font-size:13px;color:${QUILL_COLORS.text};background:${QUILL_COLORS.surface};outline:none;width:100%;box-sizing:border-box;font-family:inherit`;
      const btnStyle   = `background:${QUILL_COLORS.accent};color:${QUILL_COLORS.onAccent};border:none;border-radius:7px;padding:8px;font-size:13px;font-weight:600;cursor:pointer;width:100%`;

      // Link
      const linkPop  = makeInputPop('Insert Link');
      const linkBody = linkPop.querySelector('.rte-input-body') as HTMLElement;
      linkBody.innerHTML = `
        <div style="padding:10px 12px;display:flex;flex-direction:column;gap:8px">
          <input id="${id}-link-text" placeholder="Link text (optional)" style="${inputStyle}">
          <input id="${id}-link-url"  placeholder="https://\u2026" style="${inputStyle}">
          <button id="${id}-link-insert" style="${btnStyle}">Insert</button>
        </div>`;

      document.getElementById(`${id}-btn-link`)!.addEventListener('click', e => {
        e.stopPropagation();
        const btn = e.currentTarget as HTMLElement;
        savedRange = quill.getSelection();
        const wasOpen = linkPop.classList.contains('open');
        closeAll();
        if (wasOpen) return;
        const selText = savedRange && savedRange.length > 0 ? quill.getText(savedRange.index, savedRange.length) : '';
        (document.getElementById(`${id}-link-text`) as HTMLInputElement).value = selText;
        (document.getElementById(`${id}-link-url`)  as HTMLInputElement).value = '';
        linkPop.dataset.anchorId = `${id}-btn-link`;
        positionPop(linkPop, btn);
        linkPop.classList.add('open');
        setTimeout(() => (document.getElementById(`${id}-link-url`) as HTMLInputElement).focus(), 50);
      });
      document.getElementById(`${id}-link-insert`)!.addEventListener('click', () => {
        const url  = (document.getElementById(`${id}-link-url`)  as HTMLInputElement).value.trim();
        const text = (document.getElementById(`${id}-link-text`) as HTMLInputElement).value;
        if (!url) return;
        const r = savedRange || { index: Math.max(0, quill.getLength() - 1), length: 0 };
        if (r.length > 0) {
          quill.formatText(r.index, r.length, 'link', url);
        } else {
          const display = text || url;
          quill.insertText(r.index, display, 'link', url);
          quill.setSelection(r.index + display.length);
        }
        closeAll();
        const html = quill.root.innerHTML; lastValue.current = html; onChange(html);
      });

      // Image
      const imgPop  = makeInputPop('Insert Image');
      const imgBody = imgPop.querySelector('.rte-input-body') as HTMLElement;
      imgBody.innerHTML = `
        <div style="padding:10px 12px;display:flex;flex-direction:column;gap:8px">
          <input id="${id}-img-url" placeholder="https://\u2026 (image URL)" style="${inputStyle}">
          <input id="${id}-img-alt" placeholder="Alt text (optional)" style="${inputStyle}">
          <button id="${id}-img-insert" style="${btnStyle}">Insert</button>
        </div>`;

      document.getElementById(`${id}-btn-img`)!.addEventListener('click', e => {
        e.stopPropagation();
        const btn = e.currentTarget as HTMLElement;
        savedRange = quill.getSelection();
        const wasOpen = imgPop.classList.contains('open');
        closeAll();
        if (wasOpen) return;
        (document.getElementById(`${id}-img-url`) as HTMLInputElement).value = '';
        (document.getElementById(`${id}-img-alt`) as HTMLInputElement).value = '';
        imgPop.dataset.anchorId = `${id}-btn-img`;
        positionPop(imgPop, btn);
        imgPop.classList.add('open');
        setTimeout(() => (document.getElementById(`${id}-img-url`) as HTMLInputElement).focus(), 50);
      });
      document.getElementById(`${id}-img-insert`)!.addEventListener('click', () => {
        const url = (document.getElementById(`${id}-img-url`) as HTMLInputElement).value.trim();
        if (!url) return;
        const r = savedRange || { index: Math.max(0, quill.getLength() - 1) };
        quill.insertEmbed(r.index, 'image', url);
        quill.setSelection(r.index + 1);
        closeAll();
        const html = quill.root.innerHTML; lastValue.current = html; onChange(html);
      });

      // ── Image resize ──────────────────────────────────────────────────────
      let selImg: HTMLImageElement | null = null;
      let resizeHandle: HTMLElement | null = null;
      let rsStartX = 0, rsStartW = 0;

      const removeResizeHandle = () => {
        if (resizeHandle?.parentNode) resizeHandle.parentNode.removeChild(resizeHandle);
        resizeHandle = null;
      };

      const updateResizeHandlePos = (img: HTMLImageElement) => {
        if (!resizeHandle) return;
        const r = img.getBoundingClientRect();
        resizeHandle.style.left = (r.right - 6) + 'px';
        resizeHandle.style.top  = (r.bottom - 6) + 'px';
      };

      const selectImage = (img: HTMLImageElement) => {
        if (selImg) selImg.classList.remove('rte-img-selected');
        selImg = img;
        img.classList.add('rte-img-selected');
        removeResizeHandle();
        resizeHandle = document.createElement('div');
        resizeHandle.className = 'rte-resize-handle';
        document.body.appendChild(resizeHandle);
        updateResizeHandlePos(img);

        const onDown = (e: MouseEvent | TouchEvent) => {
          e.preventDefault();
          const touch = (e as TouchEvent).touches ? (e as TouchEvent).touches[0] : e as MouseEvent;
          rsStartX = touch.clientX;
          rsStartW = selImg!.offsetWidth;

          const onMove = (ev: MouseEvent | TouchEvent) => {
            if (!selImg) return;
            ev.preventDefault();
            const t = (ev as TouchEvent).touches ? (ev as TouchEvent).touches[0] : ev as MouseEvent;
            const newW = Math.max(40, rsStartW + (t.clientX - rsStartX));
            selImg.style.width = newW + 'px';
            selImg.style.height = 'auto';
            updateResizeHandlePos(selImg);
          };
          const onUp = () => {
            document.removeEventListener('mousemove', onMove as any);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove as any);
            document.removeEventListener('touchend', onUp);
            const html = quill.root.innerHTML; lastValue.current = html; onChange(html);
          };
          document.addEventListener('mousemove', onMove as any);
          document.addEventListener('mouseup', onUp);
          document.addEventListener('touchmove', onMove as any, { passive: false } as any);
          document.addEventListener('touchend', onUp);
        };
        resizeHandle.addEventListener('mousedown', onDown as any);
        resizeHandle.addEventListener('touchstart', onDown as any, { passive: false } as any);
      };

      const deselectImage = () => {
        if (selImg) selImg.classList.remove('rte-img-selected');
        selImg = null;
        removeResizeHandle();
      };

      quill.root.addEventListener('click', (e: MouseEvent) => {
        const t = e.target as HTMLElement;
        if (t?.tagName === 'IMG') selectImage(t as HTMLImageElement);
        else deselectImage();
      });

      document.addEventListener('click', (e: MouseEvent) => {
        const t = e.target as HTMLElement;
        if (!t?.closest('.ql-editor') && t !== resizeHandle) deselectImage();
      });

      window.addEventListener('scroll', () => { if (selImg) updateResizeHandlePos(selImg); }, true);
      window.addEventListener('resize', () => { if (selImg) updateResizeHandlePos(selImg); });
    };

    if ((window as any).Quill) { init(); }
    else {
      if (!document.getElementById('ql-js')) {
        const s = document.createElement('script');
        s.id = 'ql-js';
        s.src = 'https://cdn.quilljs.com/1.3.7/quill.min.js';
        s.onload = init;
        document.head.appendChild(s);
      } else {
        const wait = setInterval(() => { if ((window as any).Quill) { clearInterval(wait); init(); } }, 50);
      }
    }
  }, []);

  // Sync external value → editor only when not focused
  useEffect(() => {
    if (!containerRef.current || isFocused.current) return;
    if (value === lastValue.current) return;
    lastValue.current = value;
    const q = (containerRef.current as any)._quill;
    if (!q) return;
    const cb = q.getModule('clipboard');
    q.setContents(cb.convert(value || ''), 'silent');
  }, [value]);

  if (Platform.OS !== 'web') return null;
  return <div ref={containerRef} style={{ height }} />;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  maxLength = 50,
  label,
  showCharCount = true,
  editable = true,
  variant = 'default',
  showToolbar = true,
  onSubmitShortcut,
  mode = 'whatsapp',
  variables,
}) => {
  const styles = useThemedStyles(getStyles);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [webViewHeight, setWebViewHeight] = useState(320);
  const [variablePickerVisible, setVariablePickerVisible] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const isChat = variant === 'chat';
  const isProduct = mode === 'product';
  const isWeb = Platform.OS === 'web';
  const hasSelection = selection.start !== selection.end;

  // ── Native WhatsApp helpers ──────────────────────────────────────────────────
  const nativeInsert = (prefix: string, suffix = '', requiresSelection = false) => {
    if (!editable || (requiresSelection && !hasSelection)) return;
    const { start, end } = selection;
    const selected = value.substring(start, end);
    const inserted = prefix + selected + suffix;
    const next = value.substring(0, start) + inserted + value.substring(end);
    if (next.length <= maxLength) {
      onChange(next);
      const cursor = start + inserted.length;
      setTimeout(() => inputRef.current?.setNativeProps({ selection: { start: cursor, end: cursor } }), 0);
    }
  };

  // ── WebView message bridge ───────────────────────────────────────────────────
  const handleWebViewMessage = useCallback((event: any) => {
    try {
      const msg = JSON.parse(event.nativeEvent.data);
      if (msg.type === 'change') onChange(msg.html || '');
      if (msg.type === 'height') setWebViewHeight(Math.max(320, msg.height + 60));
    } catch (_) {}
  }, [onChange]);

  // ── WhatsApp toolbar ─────────────────────────────────────────────────────────
  const whatsappToolbar = (
    <View style={styles.toolbar}>
      <ToolBtn onPress={() => nativeInsert('*', '*', true)} disabled={!hasSelection} title="Bold">
        <Text style={[styles.tbLabel, { fontWeight: '700' }]}>B</Text>
      </ToolBtn>
      <ToolBtn onPress={() => nativeInsert('_', '_', true)} disabled={!hasSelection} title="Italic">
        <Text style={[styles.tbLabel, { fontStyle: 'italic' }]}>I</Text>
      </ToolBtn>
      <ToolBtn onPress={() => nativeInsert('~', '~', true)} disabled={!hasSelection} title="Strikethrough">
        <Text style={[styles.tbLabel, { textDecorationLine: 'line-through' }]}>S</Text>
      </ToolBtn>
      {variables && (
        <ToolBtn onPress={() => setVariablePickerVisible(true)} disabled={false} title="Insert value">
          <Text style={styles.tbLabel}>{'{ }'}</Text>
        </ToolBtn>
      )}
    </View>
  );

  const insertVariable = (path: string) => {
    // Cursor-only insert (no wrapping/selection requirement) — reuses the same
    // setNativeProps-based cursor placement as the bold/italic/strike buttons above.
    nativeInsert(`{{${path}}}`, '', false);
    setVariablePickerVisible(false);
  };

  const remaining = maxLength - (value?.length ?? 0);
  const isWarning = remaining < maxLength * 0.1 || remaining < 50;

  // On web, react-native-web forwards TextInput's ref straight to the underlying
  // <textarea> — listen for the native browser `paste` event to read the clipboard's HTML
  // representation (e.g. from Word/Google Docs) and convert its formatting into WhatsApp's
  // markdown syntax before it lands in the field, same as TemplateBodyEditor.tsx's message
  // body input. RN's own onPaste (native + web synthetic) only exposes plain text, so this
  // bypasses it entirely. Scoped to whatsapp mode only — product mode pastes into Quill,
  // which already preserves real HTML formatting on its own.
  useEffect(() => {
    if (Platform.OS !== 'web' || isProduct) return;
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (!node || typeof node.addEventListener !== 'function') return;

    const handleNativePaste = (event: ClipboardEvent) => {
      if (!editable) return;
      const html = event.clipboardData?.getData('text/html');
      if (!html || !html.trim()) return; // no rich content on the clipboard — let the default plain-text paste proceed

      event.preventDefault();
      const converted = htmlToWhatsAppMarkdown(html);
      if (!converted) return;

      const start = node.selectionStart ?? value.length;
      const end = node.selectionEnd ?? value.length;
      const next = `${value.slice(0, start)}${converted}${value.slice(end)}`.slice(0, maxLength);
      onChange(next);

      const cursor = Math.min(start + converted.length, maxLength);
      requestAnimationFrame(() => {
        node.setSelectionRange?.(cursor, cursor);
      });
    };

    node.addEventListener('paste', handleNativePaste);
    return () => node.removeEventListener('paste', handleNativePaste);
  }, [value, maxLength, onChange, editable, isProduct]);

  // ── Product mode ─────────────────────────────────────────────────────────────
  if (isProduct) {
    return (
      <View style={styles.container}>
        {!!label && <Text style={styles.label}>{label}</Text>}
        <View style={styles.productWrap}>
          {isWeb ? (
            <WebQuillEditor
              value={value || ''}
              onChange={onChange}
              placeholder={placeholder}
              maxLength={maxLength}
              editable={editable}
              height={320}
            />
          ) : (
            <WebView
              source={{ html: buildQuillHtml(value || '', placeholder, maxLength, editable) }}
              style={{ height: webViewHeight, width: '100%' }}
              scrollEnabled={false}
              onMessage={handleWebViewMessage}
              javaScriptEnabled
              domStorageEnabled
              mixedContentMode="compatibility"
              originWhitelist={['*']}
            />
          )}
        </View>
        {showCharCount && (
          <Text style={[styles.charCount, isWarning && styles.charCountWarn]}>
            {value?.length ?? 0}/{maxLength}
          </Text>
        )}
      </View>
    );
  }

  // ── WhatsApp mode ─────────────────────────────────────────────────────────────
  return (
    <View style={[styles.container, isChat && styles.containerChat]}>
      {!!label && <Text style={styles.label}>{label}</Text>}
      <View style={[styles.editorBox, isChat && styles.editorBoxChat]}>
        <TextInput
          ref={inputRef}
          style={[styles.input, isChat && styles.inputChat]}
          value={value}
          onChangeText={text => { if (text.length <= maxLength && editable) onChange(text); }}
          onKeyPress={(e: any) => {
            if (!editable || Platform.OS !== 'web') return;
            const { key, ctrlKey, metaKey } = e?.nativeEvent ?? {};
            if (key === 'Enter' && (ctrlKey || metaKey)) { e?.preventDefault?.(); onSubmitShortcut?.(); }
          }}
          onSelectionChange={e => setSelection(e.nativeEvent.selection)}
          placeholder={placeholder}
          placeholderTextColor={colors.textSecondary}
          multiline
          maxLength={maxLength}
          editable={editable}
          textAlignVertical="top"
        />
        {showToolbar && whatsappToolbar}
      </View>
      {showCharCount && (
        <Text style={[styles.charCount, isWarning && styles.charCountWarn]}>
          {value?.length ?? 0}/{maxLength}
        </Text>
      )}
      {variables && (
        <Modal visible={variablePickerVisible} transparent animationType="slide" onRequestClose={() => setVariablePickerVisible(false)}>
          <View style={styles.pickerOverlay}>
            <View style={styles.pickerContent}>
              <View style={styles.pickerHeader}>
                <Text style={styles.pickerTitle}>Insert Value</Text>
                <TouchableOpacity onPress={() => setVariablePickerVisible(false)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name="close" size={24} color={colors.textDark} />
                </TouchableOpacity>
              </View>
              <FlatList
                data={variables}
                keyExtractor={(group) => group.key}
                renderItem={({ item: group }) => (
                  <View style={styles.pickerGroup}>
                    <Text style={styles.pickerGroupLabel}>{group.label}</Text>
                    {group.entries.map((entry) => (
                      <TouchableOpacity key={entry.path} style={styles.pickerOption} onPress={() => insertVariable(entry.path)}>
                        <Text style={styles.pickerOptionLabel}>{entry.label}</Text>
                        <Text style={styles.pickerOptionPath}>{'{{' + entry.path + '}}'}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              />
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getStyles = () => StyleSheet.create({
  pickerOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  pickerContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
  },
  pickerHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.lg, borderBottomWidth: 1, borderBottomColor: colors.border },
  pickerTitle: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.textDark },
  pickerGroup: { paddingTop: spacing.md },
  pickerGroupLabel: { fontSize: typography.fontSize.xs, fontWeight: '600', color: colors.textLight, textTransform: 'uppercase', paddingHorizontal: spacing.lg, marginBottom: spacing.xs },
  pickerOption: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: `${String(colors.border)}40` },
  pickerOptionLabel: { fontSize: typography.fontSize.base, color: colors.textDark },
  pickerOptionPath: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: 2 },
  container: { marginBottom: spacing.md },
  containerChat: { marginBottom: 0, flex: 1 },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    color: colors.text.primary,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  productWrap: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  editorBox: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
  },
  editorBoxChat: { borderWidth: 0, backgroundColor: 'transparent' },
  input: {
    minHeight: 80,
    padding: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
    textAlignVertical: 'top',
  },
  inputChat: {
    minHeight: 44,
    backgroundColor: chat.in.bg,
    borderRadius: 24,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? spacing.sm : spacing.xs,
    fontSize: 16,
    color: colors.text.primary,
    lineHeight: 22,
    textAlignVertical: 'top',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: chat.in.border,
    fontFamily: typography.fontFamily.regular,
    includeFontPadding: false,
    textAlign: 'left',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 2,
    elevation: 1,
  },
  toolbar: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: colors.border,
    padding: spacing.xs,
    gap: spacing.xs,
  },
  tbLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  charCount: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    textAlign: 'right',
    marginTop: spacing.xs,
  },
  charCountWarn: { color: colors.error },
});
